import { height } from "@mui/system";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import {
  Label,
  Room,
  Wall,
} from "../../../features/outputEditor/components/Floorplan";
import { ButtonStates, DoorAsset, WindowAsset } from "./appState.model";

const initialState: ButtonStates = {
  drawLine: false,
  drawWindow: false,
  drawDoor: false,
  windowAssets: [],
  doorAssets: [],
  rooms: undefined,
  walls: undefined,
  labels: undefined,
  height: 0,
  width: 0,
  addAsset: {
    assetType: null,
  },
  cleanUpDone: false,
  doRoomCleanUp: true,
  undoWalls: [],
  undoRooms: [],
  undoWindowAssets: [],
  undoDoorAssets: [],
  redoWalls: [],
  redoRooms: [],
  redoWindowAssets: [],
  redoDoorAssets: [],
  undoPlot: [],
  redoPlot: [],
  showPlot: true,
  plot_boundary: undefined,
  unit: "feet",
  selectedWall: undefined,
  selectedWallOption: null,
  activeTool: null,
  lockRooms: false,
  wallTransformationUsed: false,
};

export const outputEditorSlice = createSlice({
  name: "outputEditorButtonState",
  initialState,
  reducers: {
    reset: () => initialState,
    setDrawLine: (state: ButtonStates) => {
      state.drawLine = true;
    },
    clearDrawLine: (state: ButtonStates) => {
      state.drawLine = false;
    },
    setDrawWindow: (state: ButtonStates) => {
      state.drawWindow = true;
    },
    clearDrawWindow: (state: ButtonStates) => {
      state.drawWindow = false;
    },
    setDrawDoor: (state: ButtonStates) => {
      state.drawDoor = true;
    },
    clearDrawDoor: (state: ButtonStates) => {
      state.drawDoor = false;
    },
    setWindowAssets: (
      state: ButtonStates,
      action: PayloadAction<WindowAsset[]>,
    ) => {
      state.windowAssets = action.payload;
    },
    setDoorAssets: (
      state: ButtonStates,
      action: PayloadAction<DoorAsset[]>,
    ) => {
      state.doorAssets = action.payload;
    },
    setRooms: (state: ButtonStates, action: PayloadAction<Room[]>) => {
      state.rooms = action.payload;
      console.log(state.rooms);
    },
    setHeight: (state: ButtonStates, action: PayloadAction<number>) => {
      state.height = action.payload;
    },
    setWidth: (state: ButtonStates, action: PayloadAction<number>) => {
      state.width = action.payload;
    },
    addRooms: (state: ButtonStates, action: PayloadAction<Room>) => {
      if (state.rooms === undefined) state.rooms = [];
      state?.rooms?.push(action.payload); // Directly append the new room
    },
    setWalls: (state: ButtonStates, action: PayloadAction<Wall[]>) => {
      state.walls = action.payload;
      console.log(state.walls);
    },
    addWalls: (state: ButtonStates, action: PayloadAction<Wall>) => {
      if (state.walls === undefined) {
        state.walls = [];
      }
      state.walls.push(action.payload);
    },
    addLabels: (state: ButtonStates, action: PayloadAction<Label>) => {
      if (state.labels === undefined) {
        state.labels = [];
      }
      state.labels.push(action.payload); // Directly append the new wall
    },
    setLabels: (state: ButtonStates, action: PayloadAction<Label[]>) => {
      state.labels = action.payload;
    },
    setAssetType: (
      state: ButtonStates,
      action: PayloadAction<"window" | "door" | null>,
    ) => {
      state.addAsset.assetType = action.payload;
    },
    resetRooms: (state: ButtonStates, action: PayloadAction) => {
      state.rooms = [];
    },
    resetStates: (state: ButtonStates, action: PayloadAction) => {
      state.rooms = undefined;
      state.walls = undefined;
      state.labels = undefined;
      state.undoWalls = [];
      state.undoRooms = [];
      state.undoWindowAssets = [];
      state.undoDoorAssets = [];
      state.undoPlot = [];
      state.redoWalls = [];
      state.redoRooms = [];
      state.redoWindowAssets = [];
      state.redoDoorAssets = [];
      state.redoPlot = [];
      state.plot_boundary = undefined;
      state.showPlot = true;
      state.drawLine = false;
      state.drawWindow = false;
      state.drawDoor = false;
      state.doorAssets = [];
      state.windowAssets = [];
      state.height = 0;
      state.width = 0;
      state.lockRooms = false;
      state.wallTransformationUsed = false;
      state.doRoomCleanUp = true;
      state.cleanUpDone = false;
    },
    resetLabels: (state: ButtonStates, action: PayloadAction) => {
      state.labels = [];
    },
    resetWalls: (state: ButtonStates, action: PayloadAction) => {
      state.walls = [];
    },
    pushUndoStacks: (
      state: ButtonStates,
      action: PayloadAction<{
        rooms: Room[] | undefined;
        walls: Wall[] | undefined;
        plot: Wall[] | undefined;
        doorAssets?: DoorAsset[];
        windowAssets?: WindowAsset[];
      }>,
    ) => {
      if (state.undoWalls === undefined) {
        state.undoWalls = [];
      }
      if (state.undoRooms === undefined) {
        state.undoRooms = [];
      }
      if (state.redoWalls === undefined) {
        state.redoWalls = [];
      }
      if (state.redoRooms === undefined) {
        state.redoRooms = [];
      }
      if (state.undoWindowAssets === undefined) {
        state.undoWindowAssets = [];
      }
      if (state.undoDoorAssets === undefined) {
        state.undoDoorAssets = [];
      }
      if (state.redoWindowAssets === undefined) {
        state.redoWindowAssets = [];
      }
      if (state.redoDoorAssets === undefined) {
        state.redoDoorAssets = [];
      }
      if (state.undoPlot === undefined) {
        state.undoPlot = [];
      }
      if (state.redoPlot === undefined) {
        state.redoPlot = [];
      }
      if (action.payload.walls && action.payload.walls.length > 0) {
        state.undoWalls = [...state.undoWalls, action.payload.walls];
      }
      if (action.payload.rooms && action.payload.rooms.length > 0) {
        state.undoRooms = [...state.undoRooms, action.payload.rooms];
      }
      if (action.payload.plot && action.payload.plot.length > 0) {
        state.undoPlot = [...state.undoPlot, action.payload.plot];
      }
      state.undoWindowAssets = [
        ...state.undoWindowAssets,
        action.payload.windowAssets ?? state.windowAssets ?? [],
      ];
      state.undoDoorAssets = [
        ...state.undoDoorAssets,
        action.payload.doorAssets ?? state.doorAssets ?? [],
      ];
    },
    popUndoStacks: (state: ButtonStates) => {
      if (state.undoWalls.length > 0) {
        state.undoWalls.pop();
      }
      if (state.undoRooms.length > 0) {
        state.undoRooms.pop();
      }
      if (state.undoPlot.length > 0) {
        state.undoPlot.pop();
      }
      if (state.undoWindowAssets.length > 0) {
        state.undoWindowAssets.pop();
      }
      if (state.undoDoorAssets.length > 0) {
        state.undoDoorAssets.pop();
      }
    },
    pushRedoStacks: (
      state: ButtonStates,
      action: PayloadAction<{
        rooms: Room[] | undefined;
        walls: Wall[] | undefined;
        plot: Wall[] | undefined;
        doorAssets?: DoorAsset[];
        windowAssets?: WindowAsset[];
      }>,
    ) => {
      if (state.redoWalls === undefined) {
        state.redoWalls = [];
      }
      if (state.redoRooms === undefined) {
        state.redoRooms = [];
      }
      if (state.redoPlot === undefined) {
        state.redoPlot = [];
      }
      if (state.redoWindowAssets === undefined) {
        state.redoWindowAssets = [];
      }
      if (state.redoDoorAssets === undefined) {
        state.redoDoorAssets = [];
      }
      if (action.payload.walls) {
        state.redoWalls = [...state.redoWalls, action.payload.walls];
      }
      if (action.payload.rooms) {
        state.redoRooms = [...state.redoRooms, action.payload.rooms];
      }
      if (action.payload.plot) {
        state.redoPlot = [...state.redoPlot, action.payload.plot];
      }
      state.redoWindowAssets = [
        ...state.redoWindowAssets,
        action.payload.windowAssets ?? state.windowAssets ?? [],
      ];
      state.redoDoorAssets = [
        ...state.redoDoorAssets,
        action.payload.doorAssets ?? state.doorAssets ?? [],
      ];
    },
    popRedoStacks: (state: ButtonStates) => {
      if (state.redoWalls?.length > 0) {
        state.redoWalls.pop();
      }
      if (state.redoRooms?.length > 0) {
        state.redoRooms.pop();
      }
      if (state.redoPlot?.length > 0) {
        state.redoPlot.pop();
      }
      if (state.redoWindowAssets?.length > 0) {
        state.redoWindowAssets.pop();
      }
      if (state.redoDoorAssets?.length > 0) {
        state.redoDoorAssets.pop();
      }
    },
    clearRedoStacks: (state: ButtonStates) => {
      state.redoWalls = [];
      state.redoRooms = [];
      state.redoPlot = [];
      state.redoWindowAssets = [];
      state.redoDoorAssets = [];
    },
    setCleanUpDone: (state: ButtonStates, action: PayloadAction<boolean>) => {
      state.cleanUpDone = action.payload;
    },
    setCleanUpRequired: (
      state: ButtonStates,
      action: PayloadAction<boolean>,
    ) => {
      state.doRoomCleanUp = action.payload;
    },
    setPlot: (
      state: ButtonStates,
      action: PayloadAction<Wall[] | undefined>,
    ) => {
      state.plot_boundary = action.payload;
    },
    setShowPlot: (state: ButtonStates, action: PayloadAction<boolean>) => {
      state.showPlot = action.payload;
    },
    togglePlot: (state: ButtonStates) => {
      state.showPlot = !state.showPlot;
    },
    toggleUnit: (state: ButtonStates) => {
      state.unit = state.unit === "feet" ? "meters" : "feet";
    },
    defaultUnit: (state) => {
      state.unit = "feet";
    },
    setSelectedWall: (
      state: ButtonStates,
      action: PayloadAction<Wall | undefined>,
    ) => {
      state.selectedWall = action.payload;
    },
    setSelectedWallOption: (
      state: ButtonStates,
      action: PayloadAction<string | null>,
    ) => {
      state.selectedWallOption = action.payload;
    },
    setActiveTool: (
      state: ButtonStates,
      action: PayloadAction<string | null>,
    ) => {
      // Add this reducer
      state.activeTool = action.payload;
    },
    toggleLockRooms: (state: ButtonStates) => {
      state.lockRooms = !state.lockRooms;
    },
    setLockRooms: (state: ButtonStates, action: PayloadAction<boolean>) => {
      state.lockRooms = action.payload;
    },
    setWallTransformationUsed: (state: ButtonStates) => {
      state.wallTransformationUsed = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state) => {
      state.lockRooms = false;
      state.wallTransformationUsed = false;
      state.showPlot = true;
    });
  },
});

export const {
  setDrawLine,
  clearDrawLine,
  setDrawWindow,
  clearDrawWindow,
  setDrawDoor,
  clearDrawDoor,
  setDoorAssets,
  setWindowAssets,
  setRooms,
  addRooms,
  setWalls,
  setHeight,
  setWidth,
  addWalls,
  setAssetType,
  resetRooms,
  resetLabels,
  resetWalls,
  setCleanUpDone,
  setCleanUpRequired,
  addLabels,
  setLabels,
  resetStates,
  reset,
  pushUndoStacks,
  pushRedoStacks,
  popUndoStacks,
  popRedoStacks,
  clearRedoStacks,
  setPlot,
  togglePlot,
  toggleUnit,
  setSelectedWall,
  setSelectedWallOption,
  setActiveTool,
  defaultUnit,
  toggleLockRooms,
  setLockRooms,
  setShowPlot,
  setWallTransformationUsed,
} = outputEditorSlice.actions;
export default outputEditorSlice.reducer;
